/**
 * OffscreenCanvas → Blob エンコード。
 *
 * - `encodeJpeg`: JPEG (品質 0.0〜1.0、デフォルト 0.95)
 * - `encodePng`: PNG (可逆、ヒーロー写真用)
 *
 * どちらも Canvas 依存。ブラウザ / Web Worker で実行。
 */

import { CARSENSOR_SPEC } from './types';

/**
 * ImageBitmap を JPEG にエンコード。
 */
export async function encodeJpeg(
  source: ImageBitmap,
  quality: number = CARSENSOR_SPEC.JPEG_QUALITY,
): Promise<Blob> {
  if (quality < 0 || quality > 1) {
    throw new RangeError(`JPEG quality must be 0..1, got ${quality}`);
  }
  return bitmapToBlob(source, 'image/jpeg', quality);
}

/**
 * ImageBitmap を PNG にエンコード (ヒーロー写真用)。
 */
export async function encodePng(source: ImageBitmap): Promise<Blob> {
  return bitmapToBlob(source, 'image/png');
}

/**
 * JPEG エンコード結果が 10MB を超える場合に品質を下げて再エンコードする。
 * 品質を 0.05 刻みで下げ、最低 0.70 までフォールバック。
 */
export async function encodeJpegWithSizeCap(
  source: ImageBitmap,
  initialQuality: number = CARSENSOR_SPEC.JPEG_QUALITY,
  maxBytes: number = CARSENSOR_SPEC.MAX_FILE_SIZE_BYTES,
  floorQuality: number = 0.7,
): Promise<Blob> {
  let quality = initialQuality;
  let blob = await encodeJpeg(source, quality);
  while (blob.size > maxBytes && quality > floorQuality) {
    quality = Math.max(floorQuality, quality - 0.05);
    blob = await encodeJpeg(source, quality);
  }
  return blob;
}

// ===== 内部ヘルパ =======================================================

async function bitmapToBlob(
  source: ImageBitmap,
  type: 'image/jpeg' | 'image/png',
  quality?: number,
): Promise<Blob> {
  const canvas = new OffscreenCanvas(source.width, source.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('OffscreenCanvas 2D context unavailable');
  }
  ctx.drawImage(source, 0, 0);
  if (type === 'image/jpeg' && quality !== undefined) {
    return canvas.convertToBlob({ type, quality });
  }
  return canvas.convertToBlob({ type });
}
